import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import SectionHeader from "./SectionHeader";
import { Heart, Baby, Dumbbell, Briefcase, Shield, Stethoscope, GraduationCap, User, Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

export type TestimonialProps = {
    quote: string;
    name: string;
    role: string;
    avatar: string;
    rating: number;
    index: number;
};

interface TestimonialsComponentProps {
    data: TestimonialProps[];
    autoPlay?: boolean;
    autoPlayInterval?: number;
    showControls?: boolean;
    showDots?: boolean;
    translationNamespace?: string;
}

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
    return (
        <div className="flex items-center gap-1 mt-4">
            {[...Array(5)].map((_, i) => (
                <Star
                    key={i}
                    className={`w-4 h-4 ${
                        i < rating
                            ? "fill-amber-400 text-amber-400"
                            : "fill-slate-100 text-slate-200"
                    }`}
                />
            ))}
        </div>
    );
};

const TestimonialCard: React.FC<TestimonialProps> = ({ quote, name, role, rating, index }) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });
    const { i18n } = useTranslation();
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const UserIcon = getUserIcon(role);

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 15 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
            transition={{ duration: 0.4, delay: (index % 2) * 0.1 }}
            className="h-full px-2"
            dir={isRTL ? "rtl" : "ltr"}
        >
            <div className="h-full rounded-2xl bg-white border border-slate-200/80 p-6 md:p-8 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between relative group overflow-hidden">
                
                {/* Background Subtle Quote Pattern */}
                <div className="absolute -top-2 end-4 text-slate-100 pointer-events-none group-hover:text-blue-50/80 transition-colors duration-300">
                    <Quote className="w-20 h-20 rotate-180 opacity-60" />
                </div>

                <div className="relative z-10 flex flex-col justify-between h-full">
                    
                    {/* Header: User Info */}
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] to-[#0072CE] text-white flex items-center justify-center shrink-0 shadow-xs">
                                <UserIcon className="w-6 h-6" strokeWidth={2} />
                            </div>
                            <div className="text-start min-w-0">
                                <h4 className="font-bold text-slate-900 text-base md:text-lg truncate">
                                    {name}
                                </h4>
                                <p className="text-xs font-medium text-[#0072CE]">
                                    {role}
                                </p>
                            </div>
                        </div>

                        {/* Quote Content */}
                        <p className="text-slate-600 text-sm leading-relaxed text-start mt-2">
                            {isRTL ? `«${quote}»` : `"${quote}"`}
                        </p>
                    </div>

                    {/* Star Rating */}
                    <StarRating rating={rating} />
                </div>

                {/* Bottom Border Accent on Hover */}
                <div className="absolute bottom-0 inset-x-0 h-0.5 bg-gradient-to-r from-[#0038A8] to-[#0072CE] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
        </motion.div>
    );
};

const Testimonials: React.FC<TestimonialsComponentProps> = ({
    data,
    showControls = true,
    showDots = true,
    translationNamespace = "testimonials",
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const { t, i18n } = useTranslation(translationNamespace);
    const isRTL = ["fa", "ar"].includes(i18n.language);

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIfMobile();
        window.addEventListener("resize", checkIfMobile);
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    const slidesToShow = isMobile ? 1 : 2;
    const totalSlides = Math.ceil(data.length / slidesToShow);

    const goToSlide = (slideIndex: number) => {
        setCurrentSlide(slideIndex);
    };

    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, [totalSlides]);

    const prevSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    }, [totalSlides]);

    if (!data || data.length === 0) {
        return (
            <div className="text-center py-12 text-slate-500 text-sm">
                {t("noTestimonials", "نظری برای نمایش وجود ندارد")}
            </div>
        );
    }

    return (
        <section id="testimonials" className="py-12 md:py-16 relative overflow-hidden" dir={isRTL ? "rtl" : "ltr"}>
            {/* Ambient Background Glow */}
            <div className="absolute top-0 start-1/4 w-80 h-80 bg-[#0072CE]/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 end-1/4 w-80 h-80 bg-[#0038A8]/5 rounded-full blur-3xl pointer-events-none" />

            <div className="container mx-auto px-4 max-w-6xl relative z-10">
                <div className="text-center mb-10 md:mb-14">
                    <SectionHeader
                        regularText={t("header.regularText", "نظرات")}
                        highlightedText={t("header.highlightedText", "کاربران")}
                        descriptionHighlight={t("header.descriptionHighlight", "داستان های واقعی")}
                        descriptionText={t("header.descriptionText", "از افرادی که به دلبند اعتماد می‌کنند")}
                    />
                </div>

                <div className="relative mx-auto">
                    <div className="overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{
                                transform: isRTL
                                    ? `translateX(${currentSlide * 100}%)`
                                    : `translateX(-${currentSlide * 100}%)`,
                            }}
                        >
                            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                                <div
                                    key={slideIndex}
                                    className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-2 gap-6 px-2"
                                >
                                    {data
                                        .slice(slideIndex * slidesToShow, (slideIndex + 1) * slidesToShow)
                                        .map((testimonial, index) => (
                                            <TestimonialCard
                                                key={`testimonial-${slideIndex}-${index}`}
                                                {...testimonial}
                                                index={index}
                                            />
                                        ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Controls & Pagination Dots */}
                    {(showControls || showDots) && totalSlides > 1 && (
                        <div className="flex items-center justify-center gap-4 mt-8">
                            {showControls && (
                                <button
                                    onClick={prevSlide}
                                    className="w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center shadow-2xs cursor-pointer"
                                    aria-label={t("navigation.prev", "نظر قبلی")}
                                >
                                    <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
                                </button>
                            )}

                            {showDots && (
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalSlides }).map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => goToSlide(index)}
                                            className="p-3 flex items-center justify-center cursor-pointer touch-manipulation"
                                            aria-label={`${t("navigation.goToSlide", "رفتن به اسلاید")} ${index + 1}`}
                                        >
                                            <span
                                                className={`h-2 rounded-full transition-all duration-300 block ${
                                                    currentSlide === index
                                                        ? "w-6 bg-[#0072CE]"
                                                        : "w-2 bg-slate-300 hover:bg-slate-400"
                                                }`}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {showControls && (
                                <button
                                    onClick={nextSlide}
                                    className="w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center shadow-2xs cursor-pointer"
                                    aria-label={t("navigation.next", "نظر بعدی")}
                                >
                                    <ChevronRight className="w-4 h-4 rtl:rotate-180" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Testimonials;

// Helper to resolve icon by user role
const getUserIcon = (role: string) => {
    const roleKeywords = (role || "").toLowerCase();

    if (
        roleKeywords.includes("پدر") ||
        roleKeywords.includes("مادر") ||
        roleKeywords.includes("والد") ||
        roleKeywords.includes("parent") ||
        roleKeywords.includes("father") ||
        roleKeywords.includes("mother") ||
        roleKeywords.includes("أب") ||
        roleKeywords.includes("أم") ||
        roleKeywords.includes("anne") ||
        roleKeywords.includes("baba") ||
        roleKeywords.includes("ebeveyn")
    )
        return Heart;

    if (
        roleKeywords.includes("کودک") ||
        roleKeywords.includes("فرزند") ||
        roleKeywords.includes("child") ||
        roleKeywords.includes("kid") ||
        roleKeywords.includes("طفل") ||
        roleKeywords.includes("çocuk")
    )
        return Baby;

    if (
        roleKeywords.includes("ورزش") ||
        roleKeywords.includes("تناسب") ||
        roleKeywords.includes("تمرین") ||
        roleKeywords.includes("athlete") ||
        roleKeywords.includes("fitness") ||
        roleKeywords.includes("sport") ||
        roleKeywords.includes("رياضي") ||
        roleKeywords.includes("لياقة") ||
        roleKeywords.includes("sporcu")
    )
        return Dumbbell;

    if (
        roleKeywords.includes("مدیر") ||
        roleKeywords.includes("شرکت") ||
        roleKeywords.includes("manager") ||
        roleKeywords.includes("company") ||
        roleKeywords.includes("مدير") ||
        roleKeywords.includes("yönetici")
    )
        return Briefcase;

    if (
        roleKeywords.includes("ایمنی") ||
        roleKeywords.includes("امنیت") ||
        roleKeywords.includes("safety") ||
        roleKeywords.includes("security") ||
        roleKeywords.includes("أمان") ||
        roleKeywords.includes("güvenlik")
    )
        return Shield;

    if (
        roleKeywords.includes("پزشک") ||
        roleKeywords.includes("بیمار") ||
        roleKeywords.includes("doctor") ||
        roleKeywords.includes("patient") ||
        roleKeywords.includes("طبيب") ||
        roleKeywords.includes("doktor")
    )
        return Stethoscope;

    if (
        roleKeywords.includes("معلم") ||
        roleKeywords.includes("مدرسه") ||
        roleKeywords.includes("teacher") ||
        roleKeywords.includes("school") ||
        roleKeywords.includes("معلم") ||
        roleKeywords.includes("öğretmen")
    )
        return GraduationCap;

    return User;
};
